import api from './api';

/**
 * User Service
 * Handles all user-related API calls
 */
export const userService = {
  /**
   * Get user profile
   * @param {string} userId - Optional user ID (defaults to current user)
   * @returns {Promise<Object>} - User profile data
   */
  async getProfile(userId = null) {
    if (userId) {
      return api.get(`/user/${userId}`);
    }
    return api.get('/investor/profile');
  },

  /**
   * Get user profile (alias for getProfile)
   * @param {string} userId - Optional user ID (defaults to current user)
   * @returns {Promise<Object>} - User profile data
   */
  async getUserProfile(userId = null) {
    return this.getProfile(userId);
  },

  /**
   * Update user profile
   * @param {Object} profileData - Profile data to update
   * @returns {Promise<Object>} - Updated profile
   */
  async updateProfile(userId, profileData) {
    return api.put(`/user/${userId}`, profileData);
  },

  // ==================== PROFILE PICTURE METHODS ====================

  /**
   * Upload profile picture
   * @param {File} file - Image file
   * @param {string} userId - Optional user ID (defaults to current user)
   * @returns {Promise<Object>} - Upload response with URL
   */
  async uploadProfilePicture(file, userId = null) {
    const formData = new FormData();
    formData.append('file', file);
    
    const endpoint = userId 
      ? `/user/${userId}/profile/picture` 
      : '/investor/profile/picture';
    
    return api.post(endpoint, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  /**
   * Delete profile picture
   * @param {string} userId - Optional user ID (defaults to current user)
   * @returns {Promise<Object>} - Success response
   */
  async deleteProfilePicture(userId = null) {
    const endpoint = userId 
      ? `/user/${userId}/profile/picture` 
      : '/investor/profile/picture';
    
    return api.delete(endpoint);
  },

  /**
   * Update profile picture URL (for external images)
   * @param {string} imageUrl - External image URL
   * @returns {Promise<Object>} - Updated profile
   */
  async updateProfilePicture(userId,imageUrl) {
    return api.put(`/user/${userId}/profile/picture`, { 
      profilePictureUrl: imageUrl 
    });
  },

  /**
   * Get another user's profile picture URL
   * @param {string} userId - User ID
   * @returns {Promise<string>} - Profile picture URL
   */
  async getProfilePicture(userId) {
    const profilePictureUrl = await api.get(`/user/${userId}/profile/picture`);
    return profilePictureUrl || null;
  },

  /**
   * Validate image file before upload
   * @param {File} file - Image file to validate
   * @returns {Object} - Validation result { valid, error }
   */
  validateProfilePicture(file) {
    const maxSize = 5 * 1024 * 1024; // 5MB
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    
    if (!file) {
      return { valid: false, error: 'No file provided' };
    }

    if (!allowedTypes.includes(file.type)) {
      return { 
        valid: false, 
        error: 'Invalid file type. Only JPG, PNG, GIF, and WebP are allowed.' 
      };
    }

    if (file.size > maxSize) {
      return { 
        valid: false, 
        error: `File size must be less than ${maxSize / 1024 / 1024}MB` 
      };
    }

    return { valid: true, error: null };
  },

  /**
   * Validate image file (alias for validateProfilePicture)
   * @param {File} file - Image file to validate
   * @param {number} maxSize - Maximum file size in bytes (default: 5MB)
   * @returns {Object} - Validation result { valid, error, message }
   */
  validateImageFile(file, maxSize = 5 * 1024 * 1024) {
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    const allowedExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp'];
    
    if (!file) {
      return { 
        valid: false, 
        error: 'No file provided',
        message: 'Please select an image file' 
      };
    }

    // Check file type
    if (!allowedTypes.includes(file.type)) {
      return { 
        valid: false, 
        error: 'Invalid file type',
        message: 'Only JPG, PNG, GIF, and WebP images are allowed.' 
      };
    }

    // Check file extension
    const fileName = file.name.toLowerCase();
    const hasValidExtension = allowedExtensions.some(ext => fileName.endsWith(ext));
    if (!hasValidExtension) {
      return { 
        valid: false, 
        error: 'Invalid file extension',
        message: 'File must have a valid image extension (.jpg, .png, .gif, .webp)' 
      };
    }

    // Check file size
    if (file.size > maxSize) {
      const maxSizeMB = (maxSize / 1024 / 1024).toFixed(2);
      return { 
        valid: false, 
        error: 'File too large',
        message: `File size must be less than ${maxSizeMB}MB` 
      };
    }

    // Check minimum file size (1KB)
    if (file.size < 1024) {
      return { 
        valid: false, 
        error: 'File too small',
        message: 'File appears to be corrupted or empty' 
      };
    }

    return { 
      valid: true, 
      error: null,
      message: 'File is valid' 
    };
  },

  /**
   * Convert file to base64 data URL
   * @param {File} file - File to convert
   * @returns {Promise<string>} - Base64 data URL
   */
  fileToBase64(file) {
    return new Promise((resolve, reject) => {
      if (!file) {
        reject(new Error('No file provided'));
        return;
      }

      const reader = new FileReader();
      
      reader.onload = (event) => {
        resolve(event.target.result);
      };
      
      reader.onerror = (error) => {
        reject(new Error('Failed to read file: ' + error.message));
      };
      
      reader.readAsDataURL(file);
    });
  },

  /**
   * Compress and resize image before upload (client-side)
   * @param {File} file - Original image file
   * @param {number} maxWidth - Maximum width (default: 800px)
   * @param {number} maxHeight - Maximum height (default: 800px)
   * @param {number} quality - Image quality 0-1 (default: 0.8)
   * @returns {Promise<File>} - Compressed image file
   */
  async compressProfilePicture(file, maxWidth = 800, maxHeight = 800, quality = 0.8) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;
          
          // Calculate new dimensions
          if (width > height) {
            if (width > maxWidth) {
              height *= maxWidth / width;
              width = maxWidth;
            }
          } else {
            if (height > maxHeight) {
              width *= maxHeight / height;
              height = maxHeight;
            }
          }
          
          canvas.width = width;
          canvas.height = height;
          
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          canvas.toBlob(
            (blob) => {
              const compressedFile = new File([blob], file.name, {
                type: file.type,
                lastModified: Date.now(),
              });
              resolve(compressedFile);
            },
            file.type,
            quality
          );
        };
        
        img.onerror = () => reject(new Error('Failed to load image'));
      };
      
      reader.onerror = () => reject(new Error('Failed to read file'));
    });
  },

  /**
   * Upload profile picture with validation and compression
   * @param {File} file - Image file
   * @param {Object} options - Upload options { compress, maxWidth, maxHeight, quality }
   * @returns {Promise<Object>} - Upload response with URL
   */
  async uploadProfilePictureOptimized(file, options = {}) {
    const {
      compress = true,
      maxWidth = 800,
      maxHeight = 800,
      quality = 0.8
    } = options;

    // Validate file
    const validation = this.validateProfilePicture(file);
    if (!validation.valid) {
      throw new Error(validation.error);
    }

    // Compress if enabled
    let fileToUpload = file;
    if (compress) {
      try {
        fileToUpload = await this.compressProfilePicture(file, maxWidth, maxHeight, quality);
      } catch (error) {
        console.warn('Compression failed, uploading original:', error);
        // Continue with original file if compression fails
      }
    }

    // Upload
    return this.uploadProfilePicture(fileToUpload);
  },

  /**
   * Check if user has a profile picture
   * @returns {Promise<boolean>} - True if profile picture exists
   */
  async hasProfilePicture() {
    try {
      const url = await this.getProfilePictureUrl();
      return !!url && url.length > 0;
    } catch (error) {
      return false;
    }
  },

  /**
   * Get profile picture as base64 data URL
   * @param {string} pictureUrl - Profile picture URL
   * @returns {Promise<string>} - Base64 data URL
   */
  async getProfilePictureDataUrl(pictureUrl) {
    try {
      const response = await fetch(pictureUrl);
      const blob = await response.blob();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      throw new Error('Failed to fetch profile picture');
    }
  },

  /**
   * Upload profile picture from URL (download and re-upload)
   * @param {string} imageUrl - External image URL
   * @returns {Promise<Object>} - Upload response
   */
  async uploadProfilePictureFromUrl(imageUrl) {
    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      
      // Create a file from the blob
      const filename = imageUrl.split('/').pop() || 'profile-picture.jpg';
      const file = new File([blob], filename, { type: blob.type });
      
      return this.uploadProfilePicture(file);
    } catch (error) {
      throw new Error('Failed to upload profile picture from URL');
    }
  },

  /**
   * Rotate profile picture
   * @param {File} file - Image file
   * @param {number} degrees - Rotation degrees (90, 180, 270)
   * @returns {Promise<File>} - Rotated image file
   */
  async rotateProfilePicture(file, degrees) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          
          // Set canvas dimensions based on rotation
          if (degrees === 90 || degrees === 270) {
            canvas.width = img.height;
            canvas.height = img.width;
          } else {
            canvas.width = img.width;
            canvas.height = img.height;
          }
          
          // Rotate
          ctx.translate(canvas.width / 2, canvas.height / 2);
          ctx.rotate((degrees * Math.PI) / 180);
          ctx.drawImage(img, -img.width / 2, -img.height / 2);
          
          canvas.toBlob(
            (blob) => {
              const rotatedFile = new File([blob], file.name, {
                type: file.type,
                lastModified: Date.now(),
              });
              resolve(rotatedFile);
            },
            file.type,
            0.9
          );
        };
        
        img.onerror = () => reject(new Error('Failed to load image'));
      };
      
      reader.onerror = () => reject(new Error('Failed to read file'));
    });
  },

  /**
   * Crop profile picture to square
   * @param {File} file - Image file
   * @param {number} size - Square size (default: 400px)
   * @returns {Promise<File>} - Cropped square image
   */
  async cropProfilePictureSquare(file, size = 400) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        
        img.onload = () => {
          const canvas = document.createElement('canvas');
          canvas.width = size;
          canvas.height = size;
          
          const ctx = canvas.getContext('2d');
          
          // Calculate crop dimensions (center crop)
          const minDim = Math.min(img.width, img.height);
          const sx = (img.width - minDim) / 2;
          const sy = (img.height - minDim) / 2;
          
          ctx.drawImage(img, sx, sy, minDim, minDim, 0, 0, size, size);
          
          canvas.toBlob(
            (blob) => {
              const croppedFile = new File([blob], file.name, {
                type: file.type,
                lastModified: Date.now(),
              });
              resolve(croppedFile);
            },
            file.type,
            0.9
          );
        };
        
        img.onerror = () => reject(new Error('Failed to load image'));
      };
      
      reader.onerror = () => reject(new Error('Failed to read file'));
    });
  },

  /**
   * Generate initials avatar (placeholder)
   * @param {string} firstName - First name
   * @param {string} lastName - Last name
   * @param {number} size - Avatar size (default: 200px)
   * @returns {string} - Base64 data URL
   */
  generateInitialsAvatar(firstName, lastName, size = 200) {
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    
    const ctx = canvas.getContext('2d');
    
    // Background gradient
    const gradient = ctx.createLinearGradient(0, 0, size, size);
    gradient.addColorStop(0, '#4F46E5');
    gradient.addColorStop(1, '#7C3AED');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, size, size);
    
    // Initials
    const initials = `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase();
    ctx.fillStyle = 'white';
    ctx.font = `bold ${size / 2}px Arial`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(initials, size / 2, size / 2);
    
    return canvas.toDataURL('image/png');
  },

  // ==================== END PROFILE PICTURE METHODS ====================

  /**
   * Get user preferences
   * @returns {Promise<Object>} - User preferences
   */
  async getPreferences() {
    return api.get('/investor/preferences');
  },

  /**
   * Update user preferences
   * @param {Object} preferences - Preferences to update
   * @returns {Promise<Object>} - Updated preferences
   */
  async updatePreferences(preferences) {
    return api.put('/investor/preferences', preferences);
  },

  /**
   * Get investment preferences
   * @returns {Promise<Object>} - Investment preferences
   */
  async getInvestmentPreferences() {
    return api.get('/investor/investment-preferences');
  },

  /**
   * Update investment preferences
   * @param {Object} preferences - Investment preferences to update
   * @returns {Promise<Object>} - Updated preferences
   */
  async updateInvestmentPreferences(preferences) {
    return api.put('/investor/investment-preferences', preferences);
  },

  /**
   * Get dashboard data
   * @returns {Promise<Object>} - Dashboard data
   */
  async getDashboard() {
    return api.get('/investor/dashboard');
  },

  /**
   * Get watchlist
   * @returns {Promise<Array>} - List of watchlist items
   */
  async getWatchlist() {
    return api.get('/investor/watchlist');
  },

  /**
   * Add to watchlist
   * @param {string} securityId - Security ID to watch
   * @returns {Promise<Object>} - Success response
   */
  async addToWatchlist(securityId) {
    return api.post('/investor/watchlist', { securityId });
  },

  /**
   * Remove from watchlist
   * @param {string} securityId - Security ID to remove
   * @returns {Promise<Object>} - Success response
   */
  async removeFromWatchlist(securityId) {
    return api.delete(`/investor/watchlist/${securityId}`);
  },

  /**
   * Get activity log
   * @param {number} page - Page number
   * @param {number} limit - Items per page
   * @returns {Promise<Object>} - Activity log data
   */
  async getActivityLog(page = 1, limit = 20) {
    return api.get(`/investor/activity?page=${page}&limit=${limit}`);
  },
};

export default userService;